import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import useInView from '../../hooks/useInView';

const PRINCIPLES = [
  {
    icon: 'ri-lightbulb-line',
    title: 'Clarity Over Complexity',
    desc: 'We break strategies down into plain language so you always understand the plan — no jargon, no black boxes.',
    color: 'text-brand-blue',
    bg: 'bg-blue-50 border-blue-100',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Partnership Over Transactions',
    desc: 'We work with clients, not just for them. The more we understand your business, the better the results we can deliver.',
    color: 'text-violet-500',
    bg: 'bg-violet-50 border-violet-100',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Data Over Assumptions',
    desc: 'Every recommendation we make is grounded in information — not guesses, trends, or what "everyone else is doing".',
    color: 'text-orange-500',
    bg: 'bg-orange-50 border-orange-100',
  },
  {
    icon: 'ri-refresh-line',
    title: 'Progress Over Perfection',
    desc: 'We launch, learn, and improve. Good execution now beats the perfect plan that never ships.',
    color: 'text-brand-blue-light',
    bg: 'bg-blue-50 border-blue-100',
  },
];

function AboutHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t); }, []);

  return (
    <section className="relative min-h-[60vh] flex items-center overflow-hidden bg-white border-b border-gray-100">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20light%20professional%20digital%20marketing%20workspace%20background%20soft%20teal%20glow%20minimal%20geometric%20pattern%20white%20and%20pale%20gray%20sophisticated%20atmosphere%20bright%20mood%20professional%20agency%20environment%20subtle%20depth%20and%20light&width=1920&height=800&seq=bgm_about_hero_light_001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/80 to-white"></div>
      </div>
      <div className="relative z-10 w-full max-w-site mx-auto px-4 sm:px-6 pt-36 pb-24">
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s ease 100ms, transform 0.7s ease 100ms' }}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-blue border border-brand-blue/30 bg-brand-blue/10 px-4 py-1.5 rounded-full mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse"></span>
            About Us
          </span>
          <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight max-w-3xl mb-6">
            We're Blue Globe Media
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl leading-relaxed">
            A digital marketing agency built on the belief that businesses deserve straightforward, honest, and effective digital marketing — not inflated promises.
          </p>
        </div>
      </div>
    </section>
  );
}

function WhoWeAre() {
  const { ref, visible } = useInView(0.2);
  return (
    <section className="py-20 md:py-28 bg-gray-50">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div
          ref={ref}
          className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div>
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-brand-blue mb-6">Who We Are</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-gray-900 leading-tight mb-6">
              A Small Team. A Focused Approach.
            </h2>
            <p className="text-gray-600 text-[15px] leading-relaxed mb-5">
              Blue Globe Media is a growing digital marketing agency made up of marketing professionals with experience across performance marketing, SEO, social media, and web development.
            </p>
            <p className="text-gray-600 text-[15px] leading-relaxed mb-5">
              We started Blue Globe Media to work differently — closer to clients, more transparently, and with genuine accountability for the work we deliver. We're not a large agency managing hundreds of accounts. We take on clients we can genuinely serve well, and we stay invested in their outcomes.
            </p>
            <p className="text-gray-600 text-[15px] leading-relaxed">
              Our focus is on building practical, sustainable digital growth for businesses that are serious about what they do online. No shortcuts, no fluff — just clear strategy and consistent execution.
            </p>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden w-full h-[360px]">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20creative%20digital%20marketing%20agency%20office%20space%20small%20collaborative%20team%20working%20together%20around%20a%20table%20with%20laptops%20and%20strategy%20boards%20warm%20lighting%20clean%20contemporary%20interior%20design%20natural%20light%20plants%20minimal%20decor%20professional%20startup%20workspace&width=700&height=500&seq=bgm_about_office_001&orientation=landscape"
                alt="Blue Globe Media team working"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white border border-gray-100 rounded-xl px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-brand-blue/10 border border-brand-blue/20">
                  <i className="ri-focus-3-line text-brand-blue"></i>
                </div>
                <div>
                  <p className="text-gray-900 font-display font-bold text-sm">Focused &amp; Committed</p>
                  <p className="text-gray-500 text-xs">We work with clients, not for them</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionVision() {
  const { ref, visible } = useInView(0.2);
  return (
    <section className="py-20 md:py-28 bg-white border-t border-gray-100">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div
          ref={ref}
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-brand-blue mb-10">Our Direction</span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-9">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-blue/10 border border-brand-blue/20 mb-6">
                <i className="ri-focus-3-line text-2xl text-brand-blue"></i>
              </div>
              <h3 className="font-display font-bold text-gray-900 text-2xl mb-4">Our Mission</h3>
              <p className="text-gray-600 text-[15px] leading-relaxed">
                Help businesses build a meaningful online presence through focused strategy, clear communication, and consistent execution — without the hype or inflated promises.
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-9">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-violet-50 border border-violet-100 mb-6">
                <i className="ri-eye-line text-2xl text-violet-500"></i>
              </div>
              <h3 className="font-display font-bold text-gray-900 text-2xl mb-4">Our Vision</h3>
              <p className="text-gray-600 text-[15px] leading-relaxed">
                To be the trusted digital partner for businesses that are serious about growing online — chosen not just for our skills, but for how we work and the way we treat the people we serve.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PrincipleCard({ p, delay }: { p: typeof PRINCIPLES[0]; delay: number }) {
  const { ref, visible } = useInView(0.1);
  return (
    <div
      ref={ref}
      className="bg-gray-50 border border-gray-100 rounded-2xl p-6"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms` }}
    >
      <div className={`w-11 h-11 flex items-center justify-center rounded-xl border ${p.bg} mb-5`}>
        <i className={`${p.icon} text-xl ${p.color}`}></i>
      </div>
      <h3 className="font-display font-semibold text-gray-900 text-[14px] mb-2.5 leading-snug">{p.title}</h3>
      <p className="text-gray-500 text-[13px] leading-relaxed">{p.desc}</p>
    </div>
  );
}

function WorkingStyle() {
  const { ref: headRef, visible: headVisible } = useInView(0.2);
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div
          ref={headRef}
          className="mb-14"
          style={{ opacity: headVisible ? 1 : 0, transform: headVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-brand-blue mb-5">How We Operate</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl text-gray-900 leading-tight max-w-xl">
            Principles That Guide<br />Everything We Do
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PRINCIPLES.map((p, i) => (
            <PrincipleCard key={p.title} p={p} delay={i * 80} />
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutCTA() {
  const { ref, visible } = useInView(0.2);
  return (
    <section className="py-20 bg-blue-50/50 border-t border-gray-100">
      <div
        ref={ref}
        className="max-w-site mx-auto px-4 sm:px-6 text-center"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
      >
        <h2 className="font-display font-bold text-3xl md:text-4xl text-gray-900 mb-5">
          Want to Work With Us?
        </h2>
        <p className="text-gray-600 text-[15px] max-w-lg mx-auto mb-8 leading-relaxed">
          Let's start with a simple conversation about your business and where you want to take it online.
        </p>
        <Link
          to="/contact"
          className="btn-primary text-base px-8 py-4"
        >
          Start a Conversation
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </section>
  );
}

export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <AboutHero />
      <WhoWeAre />
      <MissionVision />
      <WorkingStyle />
      <AboutCTA />
      <Footer />
    </div>
  );
}
